import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { ApiService } from '../../shared/services/api.service';
import { MaterialModule } from '../../shared/theming/material/material.module';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { environment } from '../../../environments/environment';
import { MatTableDataSource } from '@angular/material/table';
import { CommonModule } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { TableInfoWidgetComponent } from './components/table-info-widget/table-info-widget.component';
import { TableSummaryWidgetComponent } from './components/table-summary-widget/table-summary-widget.component';
import { AnalyticsWidgetComponent } from './components/analytics-widget/analytics-widget.component';
import { TimeSeriesChartComponent } from './components/time-series-chart/time-series-chart.component';
import { RagVsAgenticWidgetComponent } from './components/rag-vs-agentic-widget/rag-vs-agentic-widget.component';
import { Conversation } from '../../shared/models/conversation.model';
import { TableInfo } from '../../shared/models/table-info.model';
import { ImageInteractionsConversations } from './components/image-attach-interactions/image-attach-interactions.component';
import {
  AnalyticsData,
  CustomAnalyticsData,
  DefaultAnalyticsData,
} from '../../shared/models/analytics.model';
import { TimeSeriesData } from '../../shared/models/time-series.model';
import {
  RagVsAgenticComparisonData,
  RagVsAgenticTrendData,
} from '../../shared/models/rag-vs-agentic.model';
import { take, forkJoin, Observable, concatMap, from } from 'rxjs';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { Clipboard } from '@angular/cdk/clipboard';
import { provideNativeDateAdapter, MatOption } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { PlanNumberRevealService } from '../../shared/services/plan-number-reveal.service';
import { AdminTableSummaryService } from '../../shared/services/admin-table-summary.service';
import { TableSummary } from '../../shared/models/table-summary.model';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    MaterialModule,
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    TableInfoWidgetComponent,
    TableSummaryWidgetComponent,
    TableSummaryWidgetComponent,
    TableSummaryWidgetComponent,
    AnalyticsWidgetComponent,
    TimeSeriesChartComponent,
    RagVsAgenticWidgetComponent,
    RouterLink,
    MatOption,
    MatFormFieldModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    FormsModule,
    MatProgressBarModule,
  ],
  providers: [provideNativeDateAdapter()],
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
})
export class DashboardComponent implements OnInit {
  private apiService = inject(ApiService);
  private router = inject(Router);
  private clipboard = inject(Clipboard);
  private planNumberRevealService = inject(PlanNumberRevealService);
  private adminTableSummaryService = inject(AdminTableSummaryService);

  startDate: Date | null = null;
  endDate: Date | null = null;

  // Analytics date range
  analyticsStartDate: Date | null = null;
  analyticsEndDate: Date | null = null;
  solutionType: string = 'all';
  solutionTypeOptions = [
    { value: 'all', label: 'All' },
    { value: 'optimized-rag', label: 'Optimized RAG' },
    { value: 'rag', label: 'RAG' },
    { value: 'agent-llm', label: 'Agent LLM' },
  ];

  ratingScore: string = 'all';
  ratingScoreOptions = [
    { value: 'all', label: 'All' },
    { value: '5', label: '5' },
    { value: '4', label: '4' },
    { value: '3', label: '3' },
    { value: '2', label: '2' },
    { value: '1', label: '1' },
  ];

  ratingControl: string = '=';

  // RAG vs Agentic Snapshot (single API call for the full selected range)
  ragVsAgenticSnapshotData: RagVsAgenticComparisonData | null = null;
  isLoadingRagVsAgenticSnapshot = false;

  // RAG vs Agentic Trend
  ragVsAgenticTrendStartDate: Date | null = null;
  ragVsAgenticTrendEndDate: Date | null = null;
  ragVsAgenticTrendData: RagVsAgenticTrendData | null = null;
  isLoadingRagVsAgenticTrend = false;
  ragVsAgenticTrendProgress = 0;
  ragVsAgenticTrendProgressMessage = '';
  private ragVsAgenticTrendCache = new Map<
    string,
    RagVsAgenticComparisonData
  >();

  // Time Series separate filters
  timeSeriesStartDate: Date | null = null;
  timeSeriesEndDate: Date | null = null;
  timeSeriesSolutionType: string = 'all';
  timeSeriesRatingScore: string = 'all';
  timeSeriesRatingControl: string = '=';

  // Plan number reveal functionality
  isPlanNumberRevealed: boolean = false;
  revealCode: string = '';
  private readonly REVEAL_CODE = 'planNumberReveal';
  private readonly oidcSecurityService = inject(OidcSecurityService);

  configuration$ = this.oidcSecurityService.getConfiguration();
  resultsLength = 0;
  currentPage = 0;

  userData$ = this.oidcSecurityService.userData$;
  lastEvaluatedKey: string | null = null;

  tableInfo: TableInfo | null = null;
  isLoadingTableInfo = true;
  hasTableInfoError = false;
  voiceToVoiceTranscriptSummary: TableSummary | null = null;
  isLoadingVoiceToVoiceTranscriptSummary = true;
  hasVoiceToVoiceTranscriptSummaryError = false;
  analyticsData: AnalyticsData | null = null;
  timeSeriesData: TimeSeriesData | null = null;
  isLoadingAnalytics = false;
  isLoadingTimeSeries = false;
  timeSeriesProgress = 0;
  timeSeriesProgressMessage = '';
  isAuthenticated = false;
  isProduction = environment.production;
  conversations = new MatTableDataSource<Conversation>([]);

  // Cache for time-series data
  private timeSeriesCache = new Map<string, Map<string, CustomAnalyticsData>>();

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  private dialog = inject(MatDialog);

  ngOnInit(): void {
    this.isPlanNumberRevealed =
      this.planNumberRevealService.getIsPlanNumberRevealed();

    this.oidcSecurityService.isAuthenticated$
      .pipe(take(1))
      .subscribe(({ isAuthenticated }) => {
        this.isAuthenticated = isAuthenticated;
      });

    this.getTableInformation();
    this.loadVoiceToVoiceTranscriptSummary();
    this.initializeAnalyticsData();
    this.fetchRagVsAgenticTrendData();
    this.fetchRagVsAgenticSnapshotData();
  }

  logout(): void {
    this.oidcSecurityService.logoffLocal();
    this.router.navigate(['/login']);
  }

  openImageConversationsDialog(
    period: 'lastDay' | 'lastWeek' | 'custom'
  ): void {
    let startDate: string | undefined;
    let endDate: string | undefined;

    if (period === 'custom') {
      startDate = this.analyticsStartDate?.toISOString();
      endDate = this.analyticsEndDate?.toISOString();
    } else {
      const defaultData = this.analyticsData as DefaultAnalyticsData;
      startDate = defaultData?.[period]?.dateRange?.startDate;
      endDate = defaultData?.[period]?.dateRange?.endDate;
    }

    this.dialog.open(ImageInteractionsConversations, {
      width: '900px',
      maxHeight: '80vh',
      data: {
        startDate,
        endDate,
        period,
        isPlanNumberRevealed: this.isPlanNumberRevealed,
      },
    });
  }

  copyIdToken() {
    this.oidcSecurityService
      .getIdToken()
      .pipe(take(1))
      .subscribe((idToken) => {
        this.clipboard.copy(idToken);
      });
  }

  getTableInformation() {
    // This method can be used to fetch real table information from your API
    // Replace the endpoint with your actual table info endpoint
    this.apiService
      .get<TableInfo>('admin/get-bedrock-conversations-total-number')
      .pipe(take(1))
      .subscribe(
        (response) => {
          console.log('Table info:', response);
          this.tableInfo = response;
          this.isLoadingTableInfo = false;
        },
        (error) => {
          console.error('Error fetching table info:', error);
          this.tableInfo = null;
          this.hasTableInfoError = true;
          this.isLoadingTableInfo = false;
        }
      );
  }

  loadVoiceToVoiceTranscriptSummary(): void {
    this.adminTableSummaryService
      .getVoiceToVoiceTranscriptSummary()
      .pipe(take(1))
      .subscribe({
        next: (summary) => {
          this.voiceToVoiceTranscriptSummary = summary;
          this.isLoadingVoiceToVoiceTranscriptSummary = false;
        },
        error: (error) => {
          console.error('Error fetching Voice2Voice table summary:', error);
          this.hasVoiceToVoiceTranscriptSummaryError = true;
          this.isLoadingVoiceToVoiceTranscriptSummary = false;
        },
      });
  }

  get textConversationSummary(): TableSummary | null {
    if (!this.tableInfo) {
      return null;
    }

    return {
      totalItems: this.tableInfo.totalItems,
      tableSizeBytes: this.tableInfo.tableSizeBytes,
      tableStatus: this.tableInfo.tableStatus,
      createdAt: this.tableInfo.tableInfo?.creationDateTime,
    };
  }

  get agentLLMTextConversationCount(): number | null {
    if (!this.tableInfo) {
      return null;
    }

    return this.tableInfo.totalItems;
  }

  get totalConversationCount(): number | null {
    if (!this.tableInfo || !this.voiceToVoiceTranscriptSummary) {
      return null;
    }

    return (
      this.tableInfo.totalItems + this.voiceToVoiceTranscriptSummary.totalItems
    );
  }

  get isLoadingTotalConversationCount(): boolean {
    return (
      this.isLoadingTableInfo || this.isLoadingVoiceToVoiceTranscriptSummary
    );
  }

  initializeAnalyticsData(startDate?: Date, endDate?: Date): void {
    this.isLoadingAnalytics = true;

    const queryParams: Record<string, string> = {};

    if (startDate) {
      queryParams['startDate'] = startDate.toISOString();
    }
    if (endDate) {
      queryParams['endDate'] = endDate.toISOString();
    }
    if (this.solutionType !== 'all') {
      queryParams['solutionType'] = this.solutionType;
    }

    const queryString = Object.entries(queryParams)
      .map(([key, value]) => `${key}=${value}`)
      .join('&');

    this.apiService
      .get<AnalyticsData>(
        `admin/get-bedrock-interaction-metrics?${queryString}`
      )
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.analyticsData = response;
          this.isLoadingAnalytics = false;
        },
        error: (err) => {
          console.error('Failed to fetch analytics data:', err);
          this.isLoadingAnalytics = false;
        },
      });
  }

  refreshAnalyticsData(): void {
    this.initializeAnalyticsData(
      this.analyticsStartDate || undefined,
      this.analyticsEndDate || undefined
    );
  }

  fetchTimeSeriesData(startDate?: Date, endDate?: Date): void {
    this.isLoadingTimeSeries = true;
    this.timeSeriesProgress = 0;
    this.timeSeriesProgressMessage = 'Initializing...';

    const allDates = this.generateDateRange(startDate, endDate);

    // Generate cache key based on current filters
    const cacheKey = this.getTimeSeriesCacheKey();

    // Get or create cache for current filter combination
    if (!this.timeSeriesCache.has(cacheKey)) {
      this.timeSeriesCache.set(cacheKey, new Map());
    }
    const dateCache = this.timeSeriesCache.get(cacheKey)!;

    // Filter out dates that are already cached
    const dates = allDates.filter((date) => {
      const dateKey = this.getDateKey(date);
      return !dateCache.has(dateKey);
    });

    console.log(
      `Total days requested: ${allDates.length}, Already cached: ${allDates.length - dates.length}, Fetching: ${dates.length}`
    );

    // If all dates are cached, use cached data
    if (dates.length === 0) {
      this.timeSeriesProgress = 100;
      this.timeSeriesProgressMessage = 'Loading from cache...';
      this.buildTimeSeriesFromCache(allDates, dateCache);
      this.isLoadingTimeSeries = false;
      return;
    }

    // Create an array of API calls, one for each uncached day
    const requests: Observable<CustomAnalyticsData>[] = dates.map((date) => {
      const queryParams: Record<string, string> = {
        startDate: date.toISOString(),
        endDate: new Date(date.getTime() + 24 * 60 * 60 * 1000).toISOString(), // Next day
      };

      if (this.timeSeriesSolutionType !== 'all') {
        queryParams['solutionType'] = this.timeSeriesSolutionType;
      }
      if (this.timeSeriesRatingScore !== 'all') {
        queryParams['ratingScore'] = this.timeSeriesRatingScore;
        queryParams['ratingControl'] = this.timeSeriesRatingControl;
      }

      const queryString = Object.entries(queryParams)
        .map(([key, value]) => `${key}=${value}`)
        .join('&');

      return this.apiService
        .get<CustomAnalyticsData>(
          `admin/get-bedrock-interaction-metrics?${queryString}`
        )
        .pipe(take(1));
    });

    // Split requests into batches of 4
    const batchSize = 4;
    const batches: Observable<CustomAnalyticsData>[][] = [];
    for (let i = 0; i < requests.length; i += batchSize) {
      batches.push(requests.slice(i, i + batchSize));
    }

    console.log(
      `🚀 Starting time-series data fetch: ${batches.length} batches of ${batchSize} requests each (${dates.length} total days)`
    );

    let processedResponses = 0;

    // Execute batches sequentially, one after another
    from(batches)
      .pipe(
        concatMap((batch) => forkJoin(batch)) // Execute each batch in parallel, but batches run sequentially
      )
      .subscribe({
        next: (batchResponses) => {
          // Cache responses from this batch
          batchResponses.forEach((response, index) => {
            const dateKey = this.getDateKey(dates[processedResponses + index]);
            dateCache.set(dateKey, response);
          });

          processedResponses += batchResponses.length;
          const percentage = Math.round(
            (processedResponses / dates.length) * 100
          );

          // Update UI progress
          this.timeSeriesProgress = percentage;
          this.timeSeriesProgressMessage = `Fetching data: ${processedResponses}/${dates.length} days`;
        },
        error: (err) => {
          console.error('❌ Failed to fetch time series data:', err);
          this.isLoadingTimeSeries = false;
        },
        complete: () => {
          console.log('✅ All batches completed successfully!');
          this.timeSeriesProgress = 100;
          this.timeSeriesProgressMessage = 'Processing data...';
          // Build time series data from all dates (cached + new)
          this.buildTimeSeriesFromCache(allDates, dateCache);
          setTimeout(() => {
            this.isLoadingTimeSeries = false;
            this.timeSeriesProgressMessage = '';
          }, 500);
        },
      });
  }

  private generateProgressBar(percentage: number): string {
    const barLength = 20;
    const filledLength = Math.round((percentage / 100) * barLength);
    const emptyLength = barLength - filledLength;
    const filledBar = '█'.repeat(filledLength);
    const emptyBar = '░'.repeat(emptyLength);
    return `[${filledBar}${emptyBar}]`;
  }

  private getTimeSeriesCacheKey(): string {
    return `${this.timeSeriesSolutionType}-${this.timeSeriesRatingScore}-${this.timeSeriesRatingControl}`;
  }

  private getDateKey(date: Date): string {
    return date.toISOString().split('T')[0]; // YYYY-MM-DD
  }

  private buildTimeSeriesFromCache(
    dates: Date[],
    dateCache: Map<string, CustomAnalyticsData>
  ): void {
    this.timeSeriesData = {
      dailyMetrics: dates.map((date) => {
        const dateKey = this.getDateKey(date);
        const cachedData = dateCache.get(dateKey);
        return {
          date: date.toISOString(),
          textBasedInteractions: cachedData?.textBasedInteractions || 0,
          voiceToVoiceInteractions: cachedData?.voiceToVoiceInteractions || 0,
          totalInteractions: cachedData?.totalInteractions || 0,
          uniqueUsers: cachedData?.uniqueUsers || 0,
          averageInteractionsPerUser:
            cachedData?.averageInteractionsPerUser || 0,
        };
      }),
      dateRange: {
        startDate: dates[0].toISOString(),
        endDate: dates[dates.length - 1].toISOString(),
      },
    };
  }

  private generateDateRange(startDate?: Date, endDate?: Date): Date[] {
    const dates: Date[] = [];

    // Default to last 60 days if no dates provided
    const end = endDate || new Date();
    const start =
      startDate || new Date(end.getTime() - 60 * 24 * 60 * 60 * 1000);

    // Normalize to start of day
    const currentDate = new Date(start);
    currentDate.setHours(0, 0, 0, 0);

    const endNormalized = new Date(end);
    endNormalized.setHours(0, 0, 0, 0);

    // Generate array of dates
    while (currentDate <= endNormalized) {
      dates.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return dates;
  }

  filterAnalyticsData(): void {
    if (this.analyticsStartDate && this.analyticsEndDate) {
      this.initializeAnalyticsData(
        this.analyticsStartDate,
        this.analyticsEndDate
      );
    } else {
      this.initializeAnalyticsData();
    }
  }

  clearAnalyticsDateFilter(): void {
    // Method to clear the date filter and fetch default data
    this.analyticsStartDate = null;
    this.analyticsEndDate = null;
    this.initializeAnalyticsData();
  }

  fetchRagVsAgenticTrendData(startDate?: Date, endDate?: Date): void {
    this.isLoadingRagVsAgenticTrend = true;
    this.ragVsAgenticTrendProgress = 0;
    this.ragVsAgenticTrendProgressMessage = 'Initializing...';

    const defaultEnd = endDate || new Date();
    const defaultStart =
      startDate || new Date(defaultEnd.getTime() - 14 * 24 * 60 * 60 * 1000);

    const allDates = this.generateDateRange(defaultStart, defaultEnd);

    const dates = allDates.filter(
      (date) => !this.ragVsAgenticTrendCache.has(this.getDateKey(date))
    );

    if (dates.length === 0) {
      this.ragVsAgenticTrendProgress = 100;
      this.ragVsAgenticTrendProgressMessage = 'Loading from cache...';
      this.buildRagVsAgenticTrendFromCache(allDates);
      this.isLoadingRagVsAgenticTrend = false;
      return;
    }

    const requests: Observable<RagVsAgenticComparisonData>[] = dates.map(
      (date) => {
        const start = date.toISOString();
        const end = new Date(
          date.getTime() + 24 * 60 * 60 * 1000
        ).toISOString();
        // Add cache-busting timestamp
        const timestamp = Date.now();
        return this.apiService
          .get<RagVsAgenticComparisonData>(
            `admin/get-rag-vs-agentic-comparison?startDate=${encodeURIComponent(start)}&endDate=${encodeURIComponent(end)}&_t=${timestamp}`
          )
          .pipe(take(1));
      }
    );

    const batchSize = 4;
    const batches: Observable<RagVsAgenticComparisonData>[][] = [];
    for (let i = 0; i < requests.length; i += batchSize) {
      batches.push(requests.slice(i, i + batchSize));
    }

    let processedResponses = 0;

    from(batches)
      .pipe(concatMap((batch) => forkJoin(batch)))
      .subscribe({
        next: (batchResponses) => {
          batchResponses.forEach((response, index) => {
            const dateKey = this.getDateKey(dates[processedResponses + index]);
            if (response) {
              this.ragVsAgenticTrendCache.set(dateKey, response);
            }
          });
          processedResponses += batchResponses.length;
          const percentage = Math.round(
            (processedResponses / dates.length) * 100
          );
          this.ragVsAgenticTrendProgress = percentage;
          this.ragVsAgenticTrendProgressMessage = `Fetching data: ${processedResponses}/${dates.length} days`;
        },
        error: (err) => {
          console.error('❌ Failed to fetch RAG vs Agentic trend data:', err);
          this.isLoadingRagVsAgenticTrend = false;
        },
        complete: () => {
          this.ragVsAgenticTrendProgress = 100;
          this.ragVsAgenticTrendProgressMessage = 'Processing data...';
          this.buildRagVsAgenticTrendFromCache(allDates);
          setTimeout(() => {
            this.isLoadingRagVsAgenticTrend = false;
            this.ragVsAgenticTrendProgressMessage = '';
          }, 500);
        },
      });
  }

  private buildRagVsAgenticTrendFromCache(dates: Date[]): void {
    this.ragVsAgenticTrendData = {
      trendPoints: dates.map((date) => {
        const dateKey = this.getDateKey(date);
        const cached = this.ragVsAgenticTrendCache.get(dateKey);
        const dailyEntries = cached?.daily ?? [];

        // Sum all daily entries for this date
        const ragTotal = dailyEntries.reduce(
          (sum, e) => sum + (e.rag?.total ?? 0),
          0
        );
        const agentLlmTotal = dailyEntries.reduce(
          (sum, e) => sum + (e.agentLlm?.total ?? 0),
          0
        );
        const ragWithRatings = dailyEntries.reduce(
          (sum, e) => sum + (e.rag?.withRatings ?? 0),
          0
        );
        const agentLlmWithRatings = dailyEntries.reduce(
          (sum, e) => sum + (e.agentLlm?.withRatings ?? 0),
          0
        );

        // Weighted average for ratings
        const ragAvgRating =
          ragWithRatings > 0
            ? dailyEntries.reduce(
                (sum, e) =>
                  sum + (e.rag?.averageRating ?? 0) * (e.rag?.withRatings ?? 0),
                0
              ) / ragWithRatings
            : 0;
        const agentLlmAvgRating =
          agentLlmWithRatings > 0
            ? dailyEntries.reduce(
                (sum, e) =>
                  sum +
                  (e.agentLlm?.averageRating ?? 0) *
                    (e.agentLlm?.withRatings ?? 0),
                0
              ) / agentLlmWithRatings
            : 0;

        const ragRatingCoverage =
          ragTotal > 0 ? (ragWithRatings / ragTotal) * 100 : 0;
        const agentLlmRatingCoverage =
          agentLlmTotal > 0 ? (agentLlmWithRatings / agentLlmTotal) * 100 : 0;
        const avgRatingDifference = agentLlmAvgRating - ragAvgRating;

        return {
          date: date.toISOString(),
          ragTotal,
          ragAvgRating,
          ragRatingCoverage,
          ragWithRatings,
          agentLlmTotal,
          agentLlmAvgRating,
          agentLlmRatingCoverage,
          agentLlmWithRatings,
          avgRatingDifference,
        };
      }),
      dateRange: {
        startDate: dates[0].toISOString(),
        endDate: dates[dates.length - 1].toISOString(),
      },
    };
  }

  fetchRagVsAgenticSnapshotData(startDate?: Date, endDate?: Date): void {
    this.isLoadingRagVsAgenticSnapshot = true;
    const queryParams: Record<string, string> = {};
    if (startDate) queryParams['startDate'] = startDate.toISOString();
    if (endDate) queryParams['endDate'] = endDate.toISOString();
    // Add timestamp to prevent caching
    queryParams['_t'] = Date.now().toString();
    const queryString = Object.entries(queryParams)
      .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
      .join('&');
    const url = queryString
      ? `admin/get-rag-vs-agentic-comparison?${queryString}`
      : 'admin/get-rag-vs-agentic-comparison';

    console.log('Fetching snapshot data with URL:', url);

    this.apiService
      .get<RagVsAgenticComparisonData>(url)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          console.log('Snapshot data received:', response);
          // Deep clone to force Angular change detection
          this.ragVsAgenticSnapshotData = {
            daily: [...response.daily],
            weekly: [...response.weekly],
            dateRange: { ...response.dateRange },
          };
          this.isLoadingRagVsAgenticSnapshot = false;
        },
        error: (err) => {
          console.error('Failed to fetch RAG vs Agentic snapshot data:', err);
          this.isLoadingRagVsAgenticSnapshot = false;
        },
      });
  }

  filterRagVsAgenticTrendData(): void {
    if (this.ragVsAgenticTrendStartDate && this.ragVsAgenticTrendEndDate) {
      this.fetchRagVsAgenticTrendData(
        this.ragVsAgenticTrendStartDate,
        this.ragVsAgenticTrendEndDate
      );
      this.fetchRagVsAgenticSnapshotData(
        this.ragVsAgenticTrendStartDate,
        this.ragVsAgenticTrendEndDate
      );
    } else {
      this.fetchRagVsAgenticTrendData();
      this.fetchRagVsAgenticSnapshotData();
    }
  }

  clearRagVsAgenticTrendFilter(): void {
    this.ragVsAgenticTrendStartDate = null;
    this.ragVsAgenticTrendEndDate = null;
    this.ragVsAgenticTrendData = null;
    this.ragVsAgenticSnapshotData = null;
    // Clear cache to force fresh data fetch
    this.ragVsAgenticTrendCache.clear();
    this.fetchRagVsAgenticTrendData();
    this.fetchRagVsAgenticSnapshotData();
  }

  checkRevealCode(): void {
    if (this.revealCode === this.REVEAL_CODE) {
      this.isPlanNumberRevealed = true;
      this.planNumberRevealService.setIsPlanNumberRevealed(true);
      this.revealCode = ''; // Clear the input after successful reveal
    } else {
      this.isPlanNumberRevealed = false;
      this.planNumberRevealService.setIsPlanNumberRevealed(false);
    }
  }

  onRevealCodeChange(): void {
    // Auto-check when user types
    this.checkRevealCode();
  }

  filterTimeSeriesData(): void {
    if (this.timeSeriesStartDate && this.timeSeriesEndDate) {
      this.fetchTimeSeriesData(
        this.timeSeriesStartDate,
        this.timeSeriesEndDate
      );
    } else {
      this.fetchTimeSeriesData();
    }
  }

  clearTimeSeriesFilter(): void {
    this.timeSeriesStartDate = null;
    this.timeSeriesEndDate = null;
    this.timeSeriesSolutionType = 'all';
    this.timeSeriesRatingScore = 'all';
    this.timeSeriesRatingControl = '=';
    this.timeSeriesData = null;
  }

  refreshTimeSeriesData(): void {
    this.fetchTimeSeriesData(
      this.timeSeriesStartDate || undefined,
      this.timeSeriesEndDate || undefined
    );
  }

  formatUserName(email: string): string {
    return (
      email.split('.')[0].charAt(0).toUpperCase() + email.split('.')[0].slice(1)
    );
  }
}
